import { type FromSchemaProperty, wrapSchema, schemaMethods } from '@witkac/jschema';
import {
  type ComputedRef,
  type Ref,
  getCurrentInstance,
  watch,
  unref,
  shallowRef,
  isRef,
} from 'vue';

import { useTabs } from './tabs';
import { confirmService } from '../services/confirm.service';
import type { SchemaForm } from '../typings/schemaForm';
import type { Tab } from '../typings/tab';

export function useForm<SchemaType extends SchemaForm, ExternalType extends Record<string, any>>(
  schemaDef: SchemaType,
  model: unknown,
  external: ExternalType,
  tabs: Tab[],
): UseFormReturnType<SchemaType, ExternalType> {
  const instance = getCurrentInstance();

  let schema = wrapSchema(
    unref(schemaDef),
    model ? schemaMethods.deepCopy(unref(model)) : undefined,
    {
      external: unref(external),
      wrapAll: true,
      lazyNumbers: true,
    },
  );

  function createFieldChangedWatcher() {
    return watch(
      () => unref(schema).$model,
      () => instance?.emit('field-changed'),
      { deep: true },
    );
  }

  let changeWatcher = createFieldChangedWatcher();

  if (import.meta.env.DEV) {
    console.log('DEV MODE');
    schema = shallowRef(schema) as any;
    // aktualizacja schematu
    if (isRef(schemaDef) && isRef(external)) {
      watch(
        () => [schemaDef.value, external.value],
        () => {
          changeWatcher();
          const dirtyList = schema.value.$dirtyList([]).map((x: any) => x.$path);
          const prevModel = schema.value.$model;
          (schema.value as any).$destroy();
          const sh = wrapSchema(unref(schemaDef) as any, prevModel, {
            external: unref(external),
            wrapAll: true,
            lazyNumbers: true,
          });
          for (let i = 0; i < dirtyList.length; i++) {
            const child = sh.$child(dirtyList[i]);
            if (child) {
              child.$dirty = true;
            }
          }
          schema.value = sh;
          changeWatcher = createFieldChangedWatcher();
        },
      );
    }
    // aktualizacja modelu
    if (isRef(model)) {
      watch(
        () => model.value,
        () => {
          changeWatcher();
          (schema.value as any).$setNewData(unref(model));
          changeWatcher = createFieldChangedWatcher();
        },
      );
    }
  }

  function onSave(leave: boolean) {
    const errors = unref(schema).$validateAll();
    instance?.emit('validate', errors);

    if (leave) {
      if (errors.length > 0) {
        return confirmService
          .configurable('Formularz zawiera błedy. Czy chcesz zapisać go w obecnej formie?', {
            acceptButtonText: 'Tak - wrócę do edycji później',
            rejectButtonText: 'Nie - powróć do edycji formularza',
          })
          .then(() => instance?.emit('save', leave));
      }
    }

    return instance?.emit('save', leave);
  }

  function onCancel() {
    return confirmService
      .configurable('Zmiany w formularzu nie zostaną zapisane, czy chcesz kontynuować?', {
        acceptButtonText: 'Tak',
        rejectButtonText: 'Nie',
      })
      .then(() => instance?.emit('cancel'));
  }

  function onValidateAll() {
    return instance?.emit('validate-all');
  }

  function onContestNavigate() {
    return instance?.emit('contest-navigate');
  }

  function onOfferNavigate() {
    return instance?.emit('offer-navigate');
  }

  function onHomeNavigate() {
    return instance?.emit('home-navigate');
  }

  return {
    ...useTabs(unref(schema), tabs),
    schema,

    onSave,
    onCancel,
    onValidateAll,
    onContestNavigate,
    onOfferNavigate,
    onHomeNavigate,
  };
}

type UseFormReturnType<SchemaType, ExternalType> = {
  schema: FromSchemaProperty<SchemaType, ExternalType>;
  isFirstIndex: ComputedRef<boolean>;
  isLastIndex: ComputedRef<boolean>;
  tabs: Ref<Tab[]>;
  selectedTab: Ref<Tab>;
  nextTab: () => any;
  prevTab: () => any;
  setTab: (tab: Tab) => any;
  onSave: (leave: boolean) => any;
  onCancel: () => any;
  onValidateAll: () => any;
  onContestNavigate: () => any;
  onOfferNavigate: () => any;
  onHomeNavigate: () => any;
};
